/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import type { FieldMeta } from '@/components/forms/field.schema';
import { Badge } from '@/components/ui/Badge/badge';
import { BooleanValue } from '@/components/ui/BooleanValue/BooleanValue';
import { DateDisplay } from '@/components/ui/DateDisplay';
import { ForeignKeyCell } from '@/components/ui/ForeignKeyCell';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { ZodType } from 'zod';

// Safe meta accessor - duplicated from field-to-filter to avoid circular dependency
function getFieldMeta(field: ZodType): FieldMeta | null {
  return (field as any)._def?.meta || null;
}

export function fieldsToColumns<T>(
  fields: Record<string, ZodType>,
  options?: {
    exclude?: string[];
    customColumns?: ColumnDef<T>[];
    columnHelper?: ReturnType<typeof createColumnHelper<T>>;
    context?: any;
  },
): ColumnDef<T>[] {
  const columns: ColumnDef<T>[] = [];

  for (const [key, field] of Object.entries(fields)) {
    if (options?.exclude?.includes(key)) continue;

    const meta = getFieldMeta(field);
    if (!meta) continue;

    // Skip hidden fields
    if ('fieldType' in meta && meta.fieldType === 'hidden') continue;

    // Skip fields marked as not filterable (they might not be suitable for display either)
    // This is optional - you might want to show fields even if they're not filterable
    // if (meta.filterConfig?.filterable === false) continue;

    const fieldType = 'fieldType' in meta ? meta.fieldType : undefined;

    // Create column definition based on field type
    const column: ColumnDef<T, any> = {
      id: key,
      header: meta.label,
      accessorKey: key as keyof T,
    };

    // Add cell renderer based on field type
    switch (fieldType) {
      case 'foreignKey':
        column.cell = ({ getValue }) => (
          <ForeignKeyCell value={getValue()} entityType={meta.entity!} />
        );
        break;

      case 'foreignKeyArray':
        column.cell = ({ getValue }) => (
          <ForeignKeyCell
            value={getValue()}
            entityType={meta.entity!}
            multiple
          />
        );
        break;

      case 'date':
        column.cell = ({ getValue }) => <DateDisplay value={getValue()} />;
        break;

      case 'boolean':
        column.cell = ({ getValue }) => (
          <div className="flex items-center justify-center">
            <BooleanValue value={getValue()} iconOnly />
          </div>
        );
        break;

      case 'select':
        column.cell = ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-muted-foreground">-</span>;

          // Find the label from options if available
          const option = meta.options?.find((opt) => opt.value === value);
          return <Badge variant="outline">{option?.label || value}</Badge>;
        };
        break;

      case 'textarea':
      case 'richtext':
        column.cell = ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-muted-foreground">-</span>;
          return (
            <span
              className="block max-w-[350px] truncate"
              title={String(value)}
            >
              {String(value)}
            </span>
          );
        };
        break;

      default:
        // For text and other types, truncate long content
        column.cell = ({ getValue }) => {
          const value = getValue();
          if (value == null || value === '') {
            return <span className="text-muted-foreground">-</span>;
          }
          const strValue = String(value);
          if (strValue.length > 50) {
            return (
              <span className="block max-w-[300px] truncate" title={strValue}>
                {strValue}
              </span>
            );
          }
          return strValue;
        };
    }

    // Handle virtual fields
    if (meta.getVirtualValue) {
      // For virtual fields, we need to create a new column without accessorKey
      const virtualColumn: ColumnDef<T, any> = {
        id: key,
        header: meta.label,
        accessorFn: (row: T) => meta.getVirtualValue!(row, options?.context),
        cell: column.cell, // Keep the cell renderer if set
      };
      columns.push(virtualColumn);
    } else {
      columns.push(column);
    }
  }

  // Add custom columns if provided (replacing any existing columns with the same id)
  if (options?.customColumns) {
    const customColumnIds = new Set(
      options.customColumns.map((col) => col.id).filter(Boolean),
    );
    // Remove any generated columns that will be replaced by custom columns
    const filteredColumns = columns.filter(
      (col) => !col.id || !customColumnIds.has(col.id),
    );
    return [...filteredColumns, ...options.customColumns];
  }

  return columns;
}

// Helper function to create a column definition for row selection
export function createSelectColumn<T>(): ColumnDef<T> {
  return {
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllPageRowsSelected()}
        onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={(e) => row.toggleSelected(e.target.checked)}
      />
    ),
    size: 40,
  };
}

// Helper function to create a column definition for row actions
export function createActionsColumn<T>(
  actions: (row: T) => React.ReactNode,
): ColumnDef<T> {
  return {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => actions(row.original),
    size: 100,
  };
}
